// Mock data for Omni Sports Group website — Corporate Hub

export const heroData = {
  headline: "THE FUTURE OF",
  headlineAccent: "SPORTS TECHNOLOGY",
  subheadline: "OmniSportsGroup builds modern software and intelligence platforms for sports organizations, agents, athletes, and recruiters — from the professional level to youth programs.",
  ctaText: "Explore Our Ventures",
  ctaSecondary: "Watch Demo"
};

export const statsData = [
  { number: "500K+", label: "Athletes Tracked" },
  { number: "2,500+", label: "Teams & Schools" },
  { number: "150+", label: "Pro Agencies" },
  { number: "$2B+", label: "NIL Deals Managed" }
];

export const venturesData = [
  {
    id: 1,
    name: "OMNIAGENCY",
    tagline: "REPRESENT. NEGOTIATE. SUCCEED.",
    description: "The modern platform for sports agents and collectives to manage athletes, collaborate across teams, and streamline NIL deals, contracts, and partnerships all in one place.",
    features: [
      "Team collaboration & workflow",
      "Player management dashboard",
      "NIL deal management",
      "Contract analytics"
    ],
    color: "#004534",
    accentColor: "#D3FF62",
    status: "live",
    link: "/omniagency",
    linkType: "internal",
    featured: true
  },
  {
    id: 2,
    name: "VERIFIED",
    subtitle: "Powered by Omni",
    tagline: "TESTED. VERIFIED. TRUSTED.",
    description: "A premium athlete verification and recruiting-intelligence platform that turns real, measured performance into trusted, shareable proof — so high-school athletes get seen by the right people.",
    features: [
      "Verified athlete profiles & badges",
      "NFL-style scouting grades",
      "Recruiting tools & evaluation engine",
      "Parent portal & booking system"
    ],
    color: "#00E5FF",
    accentColor: "#00E5FF",
    status: "live",
    displayName: "Verified",
    link: "https://verifiedbyomni.app",
    linkType: "external"
  },
  {
    id: 3,
    name: "THE DOSSIER",
    tagline: "SCOUTING INTELLIGENCE FROM INSIDE THE LEAGUE.",
    description: "Weekly evaluations of NFL draft prospects and transfer-portal players, written by a former NFL scout with twenty-plus years across Combine, Pro, and College scouting. Three franchises. Three regions. One voice.",
    features: [
      "Proprietary RVS grading (0-100)",
      "Eight-scheme breakdowns",
      "Weekly draft & portal evaluations",
      "Downloadable PDF dossiers"
    ],
    color: "#FFD700",
    accentColor: "#FFD700",
    status: "live",
    displayName: "The Dossier",
    link: "https://getthedossier.app",
    linkType: "external"
  },
  {
    id: 4,
    name: "OMNIDRAFTLAB",
    tagline: "PRE-DRAFT INTELLIGENCE. STRATEGY. POSITIONING.",
    description: "A private, application-only advisory service that prepares draft-eligible athletes and their families for how the NFL actually works. Front-office-grade evaluation, strategy, and positioning — built on twenty years of NFL personnel experience.",
    features: [
      "NFL-caliber prospect evaluation",
      "Front-office draft strategy",
      "Family advisory & preparation",
      "Application-only, limited clients"
    ],
    color: "#C0C0C0",
    accentColor: "#A0A0A0",
    status: "live",
    badgeLabel: "APPLICATION ONLY",
    displayName: "OmniDraftLab",
    link: "https://omnidraftlab.com",
    linkType: "external"
  },
  {
    id: 5,
    name: "OMNIATHLETICS",
    tagline: "MANAGE. TRACK. DEVELOP.",
    description: "A platform purpose-built for high school athletic departments. Manage rosters, track eligibility, coordinate across sports, and give coaches and administrators the tools they need to run programs efficiently.",
    features: [
      "Multi-sport roster management",
      "Eligibility & compliance tracking",
      "Coach & admin collaboration",
      "Parent & athlete portals"
    ],
    color: "#D3FF62",
    accentColor: "#D3FF62",
    status: "coming_soon"
  },
  {
    id: 6,
    name: "OMNISCOUTING",
    tagline: "CONNECT. COLLABORATE. DEVELOP.",
    description: "A unified platform that connects every department within an athletic organization — from coaches and players to executives and families. Centralize athlete data, streamline communication, and enhance collaboration across professional teams, college programs, and high school athletic departments.",
    features: [
      "Cross-department collaboration",
      "Centralized athlete data & profiles",
      "Performance & development tracking",
      "Organization-wide communication"
    ],
    color: "#D3FF62",
    accentColor: "#D3FF62",
    status: "coming_soon"
  }
];

// Keep productsData as alias for backward compatibility
export const productsData = venturesData;

export const omniAgencyFeatures = {
  hero: {
    title: "OMNIAGENCY",
    subtitle: "The Future of Athlete Representation",
    description: "The modern platform for sports agents and collectives to manage athletes, collaborate across teams, and streamline NIL deals, contracts, and partnerships. Everything you need to represent athletes and close deals—all in one place."
  },
  screenshots: [
    {
      id: 1,
      title: "Dashboard",
      description: "Get a complete overview of your agency's performance, active deals, and upcoming events at a glance.",
      image: "/screenshot-dashboard.png"
    },
    {
      id: 2,
      title: "Events Calendar",
      description: "Track meetings, contract signings, media appearances, and action items in one unified calendar.",
      image: "/screenshot-events.png"
    },
    {
      id: 3,
      title: "Athletes",
      description: "Manage your entire roster with powerful filtering, search, and organization tools.",
      image: "/screenshot-athletes.png"
    },
    {
      id: 4,
      title: "Athlete Details",
      description: "Comprehensive athlete profiles with personal info, athletic data, contracts, NIL value, and more.",
      image: "/screenshot-athlete-detail.png"
    },
    {
      id: 5,
      title: "Client Management",
      description: "Manage brand relationships and client contacts with easy-to-use CRM tools.",
      image: "/screenshot-clients.png"
    },
    {
      id: 6,
      title: "NIL Deals",
      description: "Track and manage all NIL deals with detailed analytics on deal value, status, and athlete performance.",
      image: "/screenshot-nil-deals.png"
    },
    {
      id: 7,
      title: "Partnerships",
      description: "Manage sponsors, vendors, and business partners with full visibility into partnership value and status.",
      image: "/screenshot-partnerships.png"
    },
    {
      id: 8,
      title: "Contracts",
      description: "Centralize all athlete contracts and endorsement deals with document management and status tracking.",
      image: "/screenshot-contracts.png"
    },
    {
      id: 9,
      title: "Team Members",
      description: "Manage your organization's employees and staff with role-based access and department organization.",
      image: "/screenshot-team.png"
    },
    {
      id: 10,
      title: "Reports",
      description: "Generate and export comprehensive reports on users, finances, NIL deals, partnerships, and activity logs.",
      image: "/screenshot-reports.png"
    }
  ],
  features: [
    {
      title: "Team Collaboration",
      description: "Work seamlessly with your entire staff. Share deals, assign tasks, and keep everyone aligned with real-time collaboration tools built for agencies and collectives.",
      icon: "user"
    },
    {
      title: "Player Management",
      description: "Centralize all athlete information, track career milestones, manage schedules, and maintain comprehensive profiles for every client in your roster.",
      icon: "shield"
    },
    {
      title: "NIL Deal Management",
      description: "Streamline every NIL opportunity from initial contact to signed contract. Track deal flow, manage negotiations, and ensure compliance all in one place.",
      icon: "handshake"
    },
    {
      title: "Contract Analytics",
      description: "Track and analyze all your contracts in one place. Compare terms, monitor expiration dates, and keep full visibility into your deal portfolio.",
      icon: "chart"
    },
    {
      title: "Brand Partnership Management",
      description: "Manage brand relationships and track partnership opportunities. Keep all sponsor communications and deal details organized and accessible.",
      icon: "link"
    },
    {
      title: "Compliance Dashboard",
      description: "Stay ahead of NCAA, conference, and state NIL regulations with real-time compliance monitoring and automated reporting for college athletes.",
      icon: "wallet"
    }
  ],
  benefits: [
    "All athlete data centralized in one platform",
    "Collaborate seamlessly across your entire team",
    "Instant access to contracts, deals, and player info",
    "Make informed decisions with organized, accessible data"
  ]
};

export const visionData = {
  title: "OUR VISION",
  statement: "Building the infrastructure that powers the next generation of sports.",
  description: "From athlete verification and scouting intelligence to agency management and high school athletics — OmniSportsGroup is creating the technology that connects every corner of the sports world."
};

export const footerData = {
  tagline: "The Future of Athletic Achievement",
  links: {
    ventures: [
      { name: "OMNIAGENCY", href: "/omniagency" },
      { name: "VERIFIED", href: "https://verifiedbyomni.app", external: true },
      { name: "THE DOSSIER", href: "https://getthedossier.app", external: true },
      { name: "OMNIDRAFTLAB", href: "https://omnidraftlab.com", external: true },
      { name: "OMNIATHLETICS", href: "/#ventures", comingSoon: true },
      { name: "OMNISCOUTING", href: "/#ventures", comingSoon: true }
    ],
    company: [
      { name: "About", href: "/about" },
      { name: "Careers", href: "/#contact", note: "Not hiring yet\u2014but say hi!" },
      { name: "Contact", href: "/#contact" }
    ],
    resources: [
      { name: "Blog", href: "#", comingSoon: true },
      { name: "Support", href: "mailto:support@omnisportsgroup.us" },
      { name: "Partners", href: "#", comingSoon: true }
    ]
  },
  social: ["Twitter", "LinkedIn", "Instagram"],
  copyright: "\u00a9 2025 OmniSportsGroup. All rights reserved."
};

export const navLinks = [
  { name: "Ventures", href: "/#ventures" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/#contact" }
];
